import type { AxiosResponse } from 'axios';
import type { FilterRequestType } from '../../types/filterTypes';
import type {
  ChangeCostType,
  ChangeInfoType,
  DataBulkType,
  DataBulkUserType,
  DataCardType,
  DataChangeUserType,
  DataCheckoutSessionType,
  DataCustomerType,
  DataGetPaymentsType,
  DataGetUsersType,
  DataType,
  LoginType,
  ResetPasswordFirstPartType,
  ResetPasswordSecondPartType,
  ResetPasswordThirdPartType,
  DataGetPromoterAuthType,
  DeleteSubscriptionResponseType,
} from '../../types/userTypes';

import type {
  AddCodeType,
  ChangeCodeType,
  DataBulkCodeType,
  DataGetCodesType,
} from '../../types/codeTypes';

import instance from '../index';
import type { AddCouponType, ChangeCouponType, DataGetCouponType } from '../../types/couponTypes';
import type { SkiptraceStatsDataType, UserStatssDataType } from '../../types/whitelabelTypes';
import type { UserDashboardApiDataType } from '../../types/userDashboardTypes';
import type {
  DataGetFirstPromoterCampaignsType,
  DataSyncFirstPromoterCampaignsType,
  DataUpdateFirstPromoterCampaignType,
  UpdateFirstPromoterCampaignConfigType,
} from '../../types/firstPromoterCampaignTypes';

export enum AuthApiPathENUM {
  LOG_IN = '/api/auth/signin',
  SIGN_UP = '/api/auth/signup',
  SEND_CODE = '/api/auth/send-code',
  RESET_PASSWORD = '/api/auth/reset-password',
  CHANGE_OLD_PASSWORD = '/api/auth/edit-password',
}

export enum UserApiPathENUM {
  CHANGE_INFO = '/api/user/',
  AUTH_ME = '/api/user/',
  GET_ORDER_COUNT = '/api/user/count-pending',
  EDIT_PASSWORD = '/api/user/edit-password',
  GET_PAYMENTS = '/api/user/transaction',
  GET_ALL_PAYMENTS = '/api/user/transactions',
  GET_PROMOTER_AUTH = '/api/user/promoter-auth',
}

export enum PaymentApiPathENUM {
  CREATE_CARD = '/api/payment/create-card',
  CREATE_SESSION = '/api/payment/create',
  CREATE_SUBSCRIPTION = '/api/payment/subscription',
  CREATE_SUBSCRIPTION_TRIAL = '/api/payment/subscription-trial',
  DELETE_SUBSCRIPTION = 'api/payment/subscription-delete',
  GET_CARDS = '/api/payment/methods',
  AUTO_PAY = '/api/payment/auto-pay',
  SET_DEFAULT_PM = '/api/payment/set-default-pm',
  GET_CUSTOMER = '/api/payment/customer',
  DELETE_CARD = '/api/payment/card',
}

export enum AdminApiPathENUM {
  GET_ORDER_COUNT = '/api/admin/count-pending',
  GET_USER_COUNT = '/api/admin/count-users',
  GET_USERS = '/api/admin/',
  CHANGE_COST = '/api/admin/',
  EXPORT_USERS = 'api/admin/export-users',
  SKIPTRACE_STATS = 'api/admin/account/:id/skiptrace-stats',
  USER_STATS = 'api/admin/account/:id/user-stats',
  ACCOUNT_LIST = 'api/admin/account/list',
  GET_ALL_PAYMENTS = 'api/admin/transactions',
  USER_DASHBOARD_STATS = '/api/admin/user-stats',
  GET_FIRST_PROMOTER_CAMPAIGNS = '/api/admin/first-promoter-campaigns',
  SYNC_FIRST_PROMOTER_CAMPAIGNS = '/api/admin/first-promoter-campaigns/sync',
  UPDATE_FIRST_PROMOTER_CAMPAIGN = '/api/admin/first-promoter-campaigns',
}

export enum CodeApiPathENUM {
  GET_CODES = '/api/admin/codes',
  GET_CODE_COUNT = '/api/admin/count-codes',
  DELETE_CODE = '/api/admin/delete-code',
  ADD_CODE = '/api/admin/add-code',
}

export enum CouponApiPathENUM {
  GET_COUPONS = '/api/admin/coupons',
  DELETE_COUPON = '/api/admin/delete-coupon',
  ADD_COUPON = '/api/admin/add-coupon',
}

export const paymentApi = {
  checkoutSession: async (amount: number): Promise<AxiosResponse<DataCheckoutSessionType>> => {
    return instance.post(PaymentApiPathENUM.CREATE_SESSION, { amount });
  },

  checkoutSubscription: async (
    id: string,
  ): Promise<AxiosResponse<DataCheckoutSessionType>> => {
    return instance.post(PaymentApiPathENUM.CREATE_SUBSCRIPTION, { id });
  },

  checkoutTrialSubscription: async (
    id: string,
    redirectTo: string,
  ): Promise<AxiosResponse<DataCheckoutSessionType>> => {
    return instance.post(PaymentApiPathENUM.CREATE_SUBSCRIPTION_TRIAL, { id, redirectTo });
  },

  deleteSubscription: async ():
    Promise<AxiosResponse<DeleteSubscriptionResponseType>> => {
    return instance.post(PaymentApiPathENUM.DELETE_SUBSCRIPTION);
  },

  createCard: async (): Promise<AxiosResponse<DataCheckoutSessionType>> => {
    return instance.get(PaymentApiPathENUM.CREATE_CARD);
  },

  getCards: async (): Promise<AxiosResponse<DataCardType>> => {
    return instance.get(PaymentApiPathENUM.GET_CARDS);
  },

  deleteCard: async (pm: string): Promise<AxiosResponse<DataCardType>> => {
    return instance.delete(PaymentApiPathENUM.DELETE_CARD, { params: { pm } });
  },

  getCustomer: async (): Promise<AxiosResponse<DataCustomerType>> => {
    return instance.get(PaymentApiPathENUM.GET_CUSTOMER);
  },

  autoPay: async (value: {
    amount: number;
    id: string;
  }): Promise<AxiosResponse<DataCheckoutSessionType>> => {
    const { amount, id } = value;
    return instance.post(PaymentApiPathENUM.AUTO_PAY, { amount, id });
  },

  setDefaultPM: async (payMethod: string): Promise<AxiosResponse<DataCustomerType>> => {
    return instance.post(PaymentApiPathENUM.SET_DEFAULT_PM, { payMethod });
  },
};

export const authAPI = {
  login: async (info: LoginType): Promise<AxiosResponse<DataType>> => {
    return instance.post(AuthApiPathENUM.LOG_IN, info);
  },

  signUp: async (info: LoginType): Promise<AxiosResponse<DataType>> => {
    return instance.post(AuthApiPathENUM.SIGN_UP, info);
  },

  sendCode: async (info: ResetPasswordFirstPartType): Promise<AxiosResponse<string>> => {
    return instance.get(AuthApiPathENUM.SEND_CODE, { params: { ...info } });
  },

  resetPassword: async (info: ResetPasswordSecondPartType): Promise<AxiosResponse<string>> => {
    return instance.post(AuthApiPathENUM.RESET_PASSWORD, info);
  },

  changeOldPassword: async (info: ResetPasswordThirdPartType): Promise<AxiosResponse<string>> => {
    return instance.post(AuthApiPathENUM.CHANGE_OLD_PASSWORD, info);
  },
};

export const userAPI = {
  loginByToken: async (): Promise<AxiosResponse<DataType>> => {
    return instance.get(UserApiPathENUM.AUTH_ME);
  },

  getOrderCount: async (): Promise<AxiosResponse<DataBulkType>> => {
    return instance.get(UserApiPathENUM.GET_ORDER_COUNT);
  },

  changeInfoUser: async (info: ChangeInfoType): Promise<AxiosResponse<DataChangeUserType>> => {
    return instance.post(UserApiPathENUM.CHANGE_INFO, info);
  },

  editPassword: async (info: ResetPasswordThirdPartType): Promise<AxiosResponse<string>> => {
    return instance.post(UserApiPathENUM.EDIT_PASSWORD, info);
  },

  getPayments: async (filter: FilterRequestType): Promise<AxiosResponse<DataGetPaymentsType>> => {
    return instance.get(UserApiPathENUM.GET_PAYMENTS, { params: { ...filter } });
  },

  getAllPayments: async (): Promise<AxiosResponse<DataGetPaymentsType>> => {
    return instance.get(UserApiPathENUM.GET_ALL_PAYMENTS);
  },

  getPromoterAuthToken: async (): Promise<AxiosResponse<DataGetPromoterAuthType>> => {
    return instance.get(UserApiPathENUM.GET_PROMOTER_AUTH);
  },
};

export const adminAPI = {
  getOrderCount: async (): Promise<AxiosResponse<DataBulkType>> => {
    return instance.get(AdminApiPathENUM.GET_ORDER_COUNT);
  },

  getUserCount: async (): Promise<AxiosResponse<DataBulkUserType>> => {
    return instance.get(AdminApiPathENUM.GET_USER_COUNT);
  },

  getUsers: async (filter: FilterRequestType): Promise<AxiosResponse<DataGetUsersType>> => {
    return instance.get(AdminApiPathENUM.GET_USERS, { params: { ...filter } });
  },

  changeCost: async (info: ChangeCostType): Promise<AxiosResponse<string>> => {
    return instance.post(AdminApiPathENUM.CHANGE_COST, info);
  },

  exportUsers: async (): Promise<AxiosResponse<string>> => {
    return instance.get(AdminApiPathENUM.EXPORT_USERS);
  },

  getAllPayments: async (): Promise<AxiosResponse<DataGetPaymentsType>> => {
    return instance.get(AdminApiPathENUM.GET_ALL_PAYMENTS);
  },

  getFirstPromoterCampaigns: async ():
  Promise<AxiosResponse<DataGetFirstPromoterCampaignsType>> => {
    return instance.get(AdminApiPathENUM.GET_FIRST_PROMOTER_CAMPAIGNS);
  },

  syncFirstPromoterCampaigns: async ():
  Promise<AxiosResponse<DataSyncFirstPromoterCampaignsType>> => {
    return instance.post(AdminApiPathENUM.SYNC_FIRST_PROMOTER_CAMPAIGNS);
  },

  updateFirstPromoterCampaign: async (
    campaignId: number,
    payload: UpdateFirstPromoterCampaignConfigType,
  ): Promise<AxiosResponse<DataUpdateFirstPromoterCampaignType>> => {
    return instance.patch(
      `${AdminApiPathENUM.UPDATE_FIRST_PROMOTER_CAMPAIGN}/${campaignId}`,
      payload,
    );
  },

  getSkiptraceStats: async (
    id: string,
    params?: { startDate?: Date | null; endDate?: Date | null },
  ): Promise<AxiosResponse<SkiptraceStatsDataType>> => {
    const queryParams: Record<string, string> = {};
    if (params?.startDate) {
      queryParams.startDate = params.startDate.toISOString();
    }
    if (params?.endDate) {
      queryParams.endDate = params.endDate.toISOString();
    }
    return instance.get(AdminApiPathENUM.SKIPTRACE_STATS.replace(':id', id), {
      params: Object.keys(queryParams).length ? queryParams : undefined,
    });
  },

  getUserStats: async (id: string): Promise<AxiosResponse<UserStatssDataType>> => {
    return instance.get(AdminApiPathENUM.USER_STATS.replace(':id', id));
  },

  getAccountList: async () => {
    return instance.get(AdminApiPathENUM.ACCOUNT_LIST);
  },

  getUserDashboardStats: async (
    userId: string,
  ): Promise<AxiosResponse<UserDashboardApiDataType>> => {
    return instance.get(AdminApiPathENUM.USER_DASHBOARD_STATS, {
      params: { userId },
    });
  },
};

export const codeAPI = {
  getCodeCount: async (): Promise<AxiosResponse<DataBulkCodeType>> => {
    return instance.get(CodeApiPathENUM.GET_CODE_COUNT);
  },

  deleteCode: async (info: ChangeCodeType): Promise<AxiosResponse<string>> => {
    const couponId = info.couponId;
    return instance.delete(CodeApiPathENUM.DELETE_CODE, { params: { couponId } });
  },

  addCode: async (info: AddCodeType): Promise<AxiosResponse<string>> => {
    return instance.post(CodeApiPathENUM.ADD_CODE, info);
  },

  getCodes: async (filter: FilterRequestType): Promise<AxiosResponse<DataGetCodesType>> => {
    return instance.get(CodeApiPathENUM.GET_CODES, { params: { ...filter } });
  },
};

export const couponAPI = {
  deleteCoupon: async (info: ChangeCouponType): Promise<AxiosResponse<string>> => {
    const couponId = info.couponId;
    return instance.delete(CouponApiPathENUM.DELETE_COUPON, { params: { couponId } });
  },

  addCoupon: async (info: AddCouponType): Promise<AxiosResponse<string>> => {
    return instance.post(CouponApiPathENUM.ADD_COUPON, info);
  },

  getCoupons: async (filter: FilterRequestType): Promise<AxiosResponse<DataGetCouponType>> => {
    return instance.get(CouponApiPathENUM.GET_COUPONS, { params: { ...filter } });
  },
};
